import { useMemo } from 'react';

import { Button } from '@/app/components/button';
import { Icon } from '@/app/components/icons';
import { IconClose } from '@/app/components/icons/ui-actions/icon-close';
import { type TabItem, Tabs } from '@/app/components/navigation/tabs';
import { DEALER_TYPES } from '@/app/components/shared/dealer/utils/dealer-constant';
import { ICON_DEALER_TYPE_MAP } from '@/app/components/shared/dealer/utils/dealer-icons';
import { createDealerNameFormat } from '@/app/components/shared/dealer/utils/format';
import type { DealerSearch } from '@/app/components/shared/dealer/utils/types';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';

interface DealerDetailHeaderProps {
  tabs: TabItem[];
  handleChangeTab: (item: TabItem) => void;
  selectedTab: TabItem | null;
  selectedType?: DealerSearch['sale' | 'aftersale' | 'bp'] | null;
  onClose: () => void;
}

export const DealerDetailHeader = ({
  tabs,
  handleChangeTab,
  selectedTab,
  selectedType,
  onClose,
}: DealerDetailHeaderProps) => {
  const renderIcon = useMemo(() => {
    const findIcon = DEALER_TYPES.find(
      (item) => item.id === selectedTab?.value
    );
    const selectedIcon = ICON_DEALER_TYPE_MAP[findIcon?.icon || 'IconStore'];
    return selectedIcon;
  }, [selectedTab]);

  return (
    <>
      <div className="flex shrink-0 justify-between gap-x-2 pr-2 xl:items-center xl:py-3 xl:pl-4">
        <div className="flex flex-col gap-y-1">
          <div className="flex w-fit items-center gap-x-1 xl:px-2">
            <Icon
              icon={renderIcon}
              className="size-4 text-red-500"
              {...createDataAttributes({ test: 'icon' })}
            />
            <span
              className="text-caption font-noto-bold text-red-500"
              {...createDataAttributes({ test: 'type' })}
            >
              {selectedTab?.label}
            </span>
          </div>
          <p
            className="text-body-1-fixed font-noto-bold text-inline-wrap where-span:text-inline-wrap line-clamp-2"
            {...createDataAttributes({ test: 'name' })}
            dangerouslySetInnerHTML={{
              __html: createDealerNameFormat(selectedType?.name_th),
            }}
          />
        </div>
        <Button
          className="shrink-0"
          variant="tertiary"
          color="black"
          iconBefore={<IconClose />}
          aria-label="Close"
          onClick={onClose}
          {...createDataAttributes({ test: 'close' })}
        />
      </div>
      {!!selectedTab && (
        <div className={cn({ 'shrink-0 xl:mb-3 xl:px-4': tabs.length > 1 })}>
          <Tabs
            wrapperClassName="w-full"
            className="text-body-1-fixed min-h-auto w-full max-w-full overflow-visible px-0 py-2.5"
            variant="border"
            items={tabs}
            value={selectedTab?.value}
            onChange={(value, index, item) => handleChangeTab(item)}
          />
        </div>
      )}
    </>
  );
};
